import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { REASON_LABEL, label } from '../lib/labels.js';
import { dateTime, num } from '../lib/format.js';
import { EmptyState, ErrorState, LoadingState, Panel, StatusBadge } from '../components/ui.js';
import { hrefFor } from '../lib/router.js';

interface Group {
  key: 'hot' | 'warm' | 'cold';
  contacts: number;
  marketing: number;
  inSmartEmailing: number;
  blocked: Record<string, number>;
  estimated: boolean;
}
interface Potential {
  raceId: string;
  brand: string;
  generatedAt: string;
  buyers: {
    contacts: number;
    serviceReachable: number;
    marketing: number;
    inSmartEmailing: number;
  };
  groups: Group[];
  signals: {
    watchdogs: number;
    watchdogsConfirmed: number;
    watchdogsWithoutContact: number;
    webContacts: number;
    webAnonymous: number;
    webDays: number;
  };
  smartEmailing: { contacts: number; syncedAt: string | null };
  note: string | null;
}

const GROUP: Record<Group['key'], { title: string; hint: string }> = {
  hot: {
    title: 'Žhaví',
    hint: 'potvrzený hlídací pes na tento závod, nebo zájem na webu (ceny, košík, stránka závodu), ale nekoupili',
  },
  warm: {
    title: 'Teplí',
    hint: 'kupovali u značky za poslední 2 roky a jsou aktivní nebo zvolna ztrácejí zájem; tento závod nekoupili',
  },
  cold: {
    title: 'Studení',
    hint: 'mají souhlas s marketingem, ale bez nedávného nákupu a bez zájmu o závod (odhad, bez stropu četnosti a okna závodu)',
  },
};

const topReasons = (blocked: Record<string, number>): string =>
  Object.entries(blocked)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([r, n]) => `${label(REASON_LABEL, r)}: ${num(n)}`)
    .join(' · ');

/** Dosah a potenciál závodu: komu jde servis a marketing a jak „žhaví“ jsou kontakty. */
export function RacePotential({
  raceId,
  canMakeList,
}: {
  raceId: string;
  canMakeList: boolean;
}): JSX.Element {
  const q = useQuery<Potential>({
    queryKey: ['race-potential', raceId],
    queryFn: () => api.get(`/api/races/${encodeURIComponent(raceId)}/potential`),
    staleTime: 60_000,
  });
  const [made, setMade] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const make = useMutation({
    mutationFn: (group: 'hot' | 'warm') =>
      api.post<{ id: string; members: number }>(
        `/api/races/${encodeURIComponent(raceId)}/potential/list`,
        { group },
      ),
    onSuccess: (r) => {
      setError(null);
      setMade(
        `Seznam uložen: ${num(r.members)} adres. Najdeš ho v Komunikace → Rozesílky → Seznamy.`,
      );
    },
    onError: (e: Error) => {
      setMade(null);
      setError(e.message);
    },
  });

  return (
    <Panel
      title="Dosah a potenciál"
      subtitle="Komu jde servisní zpráva, komu marketing a jak jsou kontakty „žhaví“. Počítá se z objednávek, hlídacích psů, webu a zrcadla SmartEmailingu; marketing po stejných výlukách, jako při odeslání rozesílky."
    >
      {q.isLoading ? <LoadingState what="dosah závodu" /> : null}
      {q.isError ? (
        <ErrorState
          title="Dosah se nepodařilo spočítat"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : null}
      {q.data ? (
        <div className="stack" data-testid="race-potential">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Skupina</th>
                  <th className="n">Kontaktů</th>
                  <th className="n">Smí marketing</th>
                  <th className="n">Z nich ve SmartEmailingu</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr data-testid="potential-buyers">
                  <td>
                    <b>Koupili tento závod</b>
                    <br />
                    <small className="muted">
                      servisní zprávy dostanou bez souhlasu (dosažitelných{' '}
                      {num(q.data.buyers.serviceReachable)})
                    </small>
                  </td>
                  <td className="n">{num(q.data.buyers.contacts)}</td>
                  <td className="n">{num(q.data.buyers.marketing)}</td>
                  <td className="n">{num(q.data.buyers.inSmartEmailing)}</td>
                  <td />
                </tr>
                {q.data.groups.map((g) => (
                  <tr key={g.key} data-testid={`potential-${g.key}`}>
                    <td>
                      <b>{GROUP[g.key].title}</b>
                      <br />
                      <small className="muted">{GROUP[g.key].hint}</small>
                      {Object.keys(g.blocked).length > 0 ? (
                        <>
                          <br />
                          <small className="muted">Nesmí: {topReasons(g.blocked)}</small>
                        </>
                      ) : null}
                    </td>
                    <td className="n">{num(g.contacts)}</td>
                    <td className="n">
                      {g.estimated ? '≈ ' : ''}
                      {num(g.marketing)}
                    </td>
                    <td className="n">
                      {g.key === 'cold'
                        ? '—'
                        : `${g.estimated ? '≈ ' : ''}${num(g.inSmartEmailing)}`}
                    </td>
                    <td>
                      {canMakeList && g.key !== 'cold' && g.marketing > 0 ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={make.isPending}
                          onClick={() => make.mutate(g.key as 'hot' | 'warm')}
                        >
                          Uložit jako seznam
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {made ? (
            <StatusBadge tone="ok" icon="check">
              {made}
            </StatusBadge>
          ) : null}
          {error ? (
            <ErrorState title="Seznam se nepodařilo uložit" error={new Error(error)} />
          ) : null}
          <dl className="kv">
            <dt>Hlídací psi</dt>
            <dd>
              {num(q.data.signals.watchdogs)}, potvrzených {num(q.data.signals.watchdogsConfirmed)}
              {q.data.signals.watchdogsWithoutContact > 0
                ? `, bez spárovaného kontaktu ${num(q.data.signals.watchdogsWithoutContact)} (nejsou v žádné skupině)`
                : ''}
            </dd>
            <dt>Web (posledních {q.data.signals.webDays} dní)</dt>
            <dd>
              rozpoznaných kontaktů {num(q.data.signals.webContacts)}, anonymních návštěvníků{' '}
              {num(q.data.signals.webAnonymous)} (e-mailem je nejde oslovit)
            </dd>
            <dt>SmartEmailing</dt>
            <dd>
              {num(q.data.smartEmailing.contacts)} kontaktů značky {q.data.brand} s platným
              členstvím
              {q.data.smartEmailing.syncedAt
                ? `, naposledy aktualizováno ${dateTime(q.data.smartEmailing.syncedAt)}`
                : ''}
            </dd>
          </dl>
          {q.data.note ? (
            <EmptyState title="Počty ze SmartEmailingu zatím neznáme">{q.data.note}</EmptyState>
          ) : null}
          <p className="note">
            Skupiny se nepřekrývají (koupili → žhaví → teplí → studení). Uložený seznam obsahuje jen
            ty, kdo v tu chvíli smí dostat marketing, a použiješ ho jako cílení rozesílky{' '}
            <a href={hrefFor('communication')}>v Komunikaci</a>. Kdo už tento závod koupil, v něm
            není.
          </p>
        </div>
      ) : null}
    </Panel>
  );
}

interface Recommendations {
  raceName: string;
  days: number;
  interested: number;
  eligible: number;
  blocked: Record<string, number>;
  anonymous: number;
  estimated: boolean;
  items: Array<{
    contactId: string;
    email: string;
    views: number;
    strong: number;
    lastAt: string;
    paths: string[];
    reason: string;
  }>;
}

/**
 * Doporučení podle webu: kdo se zajímal o tento závod (i když dostal e-mail o jiném), nekoupil a smí dostat
 * marketing. Závod se pozná z cesty stránky, takže to funguje bez úpravy webu.
 */
export function RaceRecommendations({
  raceId,
  canMakeList,
}: {
  raceId: string;
  canMakeList: boolean;
}): JSX.Element {
  const [days, setDays] = useState(30);
  const q = useQuery<Recommendations>({
    queryKey: ['race-recommendations', raceId, days],
    queryFn: () => api.get(`/api/races/${encodeURIComponent(raceId)}/recommendations?days=${days}`),
    staleTime: 60_000,
  });
  const [made, setMade] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const make = useMutation({
    mutationFn: () =>
      api.post<{ id: string; members: number }>(
        `/api/races/${encodeURIComponent(raceId)}/recommendations/list`,
        { days },
      ),
    onSuccess: (r) => {
      setError(null);
      setMade(
        `Seznam uložen: ${num(r.members)} adres. Najdeš ho v Komunikace → Rozesílky → Seznamy.`,
      );
    },
    onError: (e: Error) => {
      setMade(null);
      setError(e.message);
    },
  });
  return (
    <Panel
      title="Doporučení podle webu"
      subtitle="Lidé, kteří se na webu zajímali o tento závod (stránky, ceny, košík), ale nekoupili. Hodí se i pro ty, kdo dostali e-mail o jiném závodu."
      action={
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label="Období zájmu"
        >
          {[7, 14, 30, 60].map((d) => (
            <option key={d} value={d}>
              posledních {d} dní
            </option>
          ))}
        </select>
      }
    >
      {q.isLoading ? <LoadingState what="doporučení" /> : null}
      {q.isError ? (
        <ErrorState
          title="Doporučení se nepodařilo spočítat"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : null}
      {q.data ? (
        <div className="stack" data-testid="race-recommendations">
          <p>
            Zájem projevilo <b>{num(q.data.interested)}</b> rozpoznaných lidí, marketing smí dostat{' '}
            <b>
              {q.data.estimated ? '≈ ' : ''}
              {num(q.data.eligible)}
            </b>
            .
            {Object.keys(q.data.blocked).length > 0 ? (
              <>
                {' '}
                <small className="muted">Nesmí: {topReasons(q.data.blocked)}</small>
              </>
            ) : null}
          </p>
          {q.data.items.length === 0 ? (
            <EmptyState title="Zatím nikdo, koho by šlo oslovit">
              Zájem se pozná z návštěv webu rozpoznaných lidí (po přihlášení, nebo po kliku z našeho
              e-mailu).
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Kontakt</th>
                    <th>Proč doporučujeme</th>
                    <th>Naposledy</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.items.map((i) => (
                    <tr key={i.contactId}>
                      <td>
                        <a href={hrefFor('customers', i.contactId)}>{i.email}</a>
                      </td>
                      <td>
                        {i.reason}
                        {i.paths.length > 0 ? (
                          <>
                            <br />
                            <small className="muted">
                              <code>{i.paths.join(' · ')}</code>
                            </small>
                          </>
                        ) : null}
                      </td>
                      <td>{dateTime(i.lastAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {canMakeList && q.data.eligible > 0 ? (
            <div className="row">
              <button
                type="button"
                className="btn pri"
                disabled={make.isPending}
                onClick={() => make.mutate()}
              >
                Uložit všechny jako seznam
              </button>
            </div>
          ) : null}
          {made ? (
            <StatusBadge tone="ok" icon="check">
              {made}
            </StatusBadge>
          ) : null}
          {error ? (
            <ErrorState title="Seznam se nepodařilo uložit" error={new Error(error)} />
          ) : null}
          <p className="note">
            Anonymních návštěvníků stránek tohoto závodu: {num(q.data.anonymous)} (bez adresy je
            nejde oslovit). Závod se pozná z adresy stránky, ukázky cest jsou u každého řádku. Nic
            se neposílá samo; seznam použiješ jako cílení rozesílky.
          </p>
        </div>
      ) : null}
    </Panel>
  );
}
